// Schema JSON dei flow wizard configurabili da frontend ("Flow v1").
//
// Questo file è il CONTRATTO tra tre mondi:
//   1. la colonna `graph` (JSON) della tabella dei flow su api-dev
//   2. il builder in /impostazioni/flow (produce questo JSON)
//   3. `compileFlow()` (lo trasforma in una `WizardDef` eseguibile dal
//      `WizardModal` già esistente)
//
// REGOLA DI PROGETTO: qui dentro non entra MAI codice. Nessuna stringa di
// JavaScript, nessuna espressione arbitraria, niente `eval`. Un flow può solo:
//   - leggere valori dallo scope (dot notation)
//   - valutare condizioni con il DSL AND/OR (lo stesso dei trigger su api-dev)
//   - invocare azioni registrate per nome (whitelist, vedi `actions.ts`)
//   - renderizzare controlli registrati per nome (whitelist, vedi `controls.ts`)
// Tutto ciò che serve in più è una funzione TS registrata da uno sviluppatore.

// ─── Condizioni ───────────────────────────────────────────────────────────────
// Stessa forma del DSL di `TriggerConditionEvaluator.php` su api-dev, così
// l'admin impara una sola sintassi e le regole restano portabili client/server.

export type FlowOperator =
  | '==' | 'eq' | 'equals'
  | '!=' | 'ne' | 'not_equals'
  | '>' | 'gt'
  | '>=' | 'gte'
  | '<' | 'lt'
  | '<=' | 'lte'
  | 'in' | 'not_in'
  | 'contains'
  | 'is_null' | 'null'
  | 'not_null' | 'not_nullable'
  | 'changed'
  | 'matches' | 'regex'
  | 'before' | 'after' | 'between'
  | 'starts_with' | 'ends_with'
  | 'empty' | 'not_empty'

/** Riferimento a un altro campo dello scope, per confronti campo-a-campo. */
export interface FlowFieldRef {
  $field: string
}

export interface FlowLeafCondition {
  /** Path nello scope: `item.x`, `answers.y.z`, `$answer.campo`, o campo nudo (⇒ `item.<campo>`). */
  field: string
  operator: FlowOperator
  /** Valore atteso. Può essere un template `{{...}}` o un `{ $field: 'path' }`. */
  value?: unknown
  options?: { case_sensitive?: boolean }
}

export interface FlowAndCondition { and: FlowCondition[] }
export interface FlowOrCondition { or: FlowCondition[] }
/** ESTENSIONE rispetto al DSL PHP: se la porti lato server, aggiungila anche là. */
export interface FlowNotCondition { not: FlowCondition }

export type FlowCondition =
  | FlowLeafCondition
  | FlowAndCondition
  | FlowOrCondition
  | FlowNotCondition
  /** Array nudo = AND implicito (come nel DSL PHP). */
  | FlowCondition[]

// ─── Azioni ───────────────────────────────────────────────────────────────────

export interface FlowActionSpec {
  /** Nome registrato in `actions.ts`. Se non esiste, il flow non è pubblicabile. */
  type: string
  /** Parametri dell'azione. I valori stringa passano da `resolveTemplate`. */
  config?: Record<string, unknown>
  /** Se presente, l'azione parte solo quando la condizione è vera. */
  when?: FlowCondition
}

// ─── Campi e controlli ────────────────────────────────────────────────────────

export interface FlowOption {
  value: unknown
  label: string
  /** Solo per il controllo `buttons`: testo secondario e icona FontAwesome. */
  description?: string
  icon?: string
  color?: string
  /**
   * Le altre colonne della riga di origine, conservate come sono.
   *
   * Servono a `optionsWhere`, che valuta la condizione con l'opzione sullo
   * scope: filtrare le officine per sede vuole `option.sede_id`, e senza le
   * colonne extra quel path risolverebbe a vuoto in silenzio. I controlli
   * leggono solo le chiavi che conoscono, quindi le altre non danno fastidio.
   */
  [colonna: string]: unknown
}

export interface FlowField {
  /** Chiave della risposta: la risposta del nodo è `{ [name]: valore }`. */
  name: string
  /** Nome di un controllo registrato (`CFormInput`, `CDatePicker`, `buttons`, ...). */
  control: string
  label?: string
  hint?: string
  /** Larghezza in colonne bootstrap (1-12). Default 12. */
  cols?: number
  /** Props passate al controllo, filtrate sulla whitelist del controllo. */
  props?: Record<string, unknown>
  /** Opzioni statiche (select/radio/multiselect/buttons). */
  options?: FlowOption[] | Record<string, string>
  /** Opzioni lette a runtime dallo scope, es. `setting.agendaReparti`. */
  optionsFrom?: string
  /**
   * Filtra le opzioni. Valutata UNA VOLTA PER OPZIONE, con l'opzione sotto
   * esame disponibile su `option.value` / `option.label`. Serve alle tendine
   * dipendenti da un altro campo, per esempio "se il reparto è Officina mostra
   * solo le ricezioni NL e FL":
   *
   *   { or: [
   *       { field: 'answers.dati.selectRepartoId', operator: '!=', value: 3 },
   *       { field: 'option.value', operator: 'in', value: ['NL', 'FL'] },
   *   ]}
   *
   * Si legge: "o il reparto non è Officina (nessun filtro), oppure l'opzione è
   * fra quelle ammesse".
   *
   * Quando il filtro cambia e il valore già scelto non è più ammesso, la
   * risposta viene azzerata: lasciarla porterebbe a salvare un valore che
   * l'utente non vede più a schermo.
   */
  optionsWhere?: FlowCondition
  /** Valore iniziale, anche template: `"{{item.noteAccettazione}}"`. */
  default?: unknown
  required?: boolean
  requiredMessage?: string
  visible_when?: FlowCondition
  disabled_when?: FlowCondition
  /**
   * Nome di una VARIABILE del flow in cui far confluire questo campo, per
   * esempio `statoRicezioneId`. Serve quando lo stesso dato viene raccolto da
   * più rami alternativi: senza, alla fine bisogna sapere da quale nodo è
   * passato l'utente per andarlo a leggere in `answers.<nodo>.<campo>`.
   *
   *   nodo `ricezione-point`    campo `statoRicezioneId`  exportTo: statoRicezioneId
   *   nodo `ricezione-officina` campo `statoRicezioneId`  exportTo: statoRicezioneId
   *   → ovunque:  {{vars.statoRicezioneId}}
   *
   * Le variabili NON sono uno stato che si scrive: sono DERIVATE dai campi che
   * le dichiarano, considerando solo i nodi effettivamente attraversati (vedi
   * `deriveFlowVars`). Se l'utente torna indietro e cambia ramo, la variabile
   * segue da sola, perché non c'è nessun valore rimasto scritto da qualche
   * parte a raccontare un percorso che non esiste più.
   *
   * Ammesso anche nella forma esplicita `vars.statoRicezioneId`.
   */
  exportTo?: string
}

// ─── Nodi ─────────────────────────────────────────────────────────────────────

/**
 * `form`    → uno o più campi, la risposta è un oggetto chiave→valore
 * `confirm` → riepilogo in sola lettura (nessuna risposta)
 *
 * Nota: NON esiste un nodo `action` senza UI. Sarebbe un nodo invisibile che si
 * auto-avanza, e tornando "Indietro" ci si finirebbe dentro in loop. Le azioni
 * si attaccano a `on_next` di un nodo o a `on_complete` del flow.
 */
export type FlowNodeType = 'form' | 'confirm'

export interface FlowConfirmRow {
  label: string
  /** Template risolto sullo scope: `"{{answers.ore.previste}}"`. */
  value: unknown
}

export interface FlowValidationRule {
  /** Deve risultare VERA perché il nodo sia valido. */
  require: FlowCondition
  /** Messaggio mostrato quando `require` è falsa. */
  message: string
}

export interface FlowNode {
  id: string
  type?: FlowNodeType
  title?: string
  description?: string
  /** Solo `type: 'form'`. */
  fields?: FlowField[]
  /** Solo `type: 'confirm'`. */
  rows?: FlowConfirmRow[]
  /** Se vera, il nodo viene saltato in navigazione. */
  skip_when?: FlowCondition
  validate?: FlowValidationRule[]
  /** Azioni eseguite (e attese) al click su "Avanti". */
  on_next?: FlowActionSpec[]
  /** Avanza da solo appena l'utente sceglie (utile con `buttons`/radio). */
  auto_advance?: boolean
  /** Posizione sul canvas, usata solo dal builder grafico. */
  ui?: { x?: number; y?: number }
}

// ─── Archi ────────────────────────────────────────────────────────────────────

/** Destinazione sentinella: termina il flow ed esegue `on_complete`. */
export const FLOW_END = '$end'

export interface FlowEdge {
  from: string
  /** Id di un nodo, oppure `'$end'`. */
  to: string
  /** Primo arco la cui condizione è vera (o senza condizione) vince. */
  when?: FlowCondition
  /** Etichetta mostrata sull'arco nel builder grafico. */
  label?: string
}

// ─── Flow ─────────────────────────────────────────────────────────────────────

export interface FlowInput {
  name: string
  type?: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'any'
  required?: boolean
  /** Path da cui prendere il valore nello scope dell'host. */
  from?: string
  default?: unknown
}

/**
 * Variabile del flow alimentata da un valore che arriva dalla PAGINA, non da
 * una risposta dell'utente.
 *
 * Serve quando un dato è già noto prima di partire — la sede selezionata, la
 * targa, il chilometraggio — e il passo che lo chiederebbe viene saltato con
 * `skip_when`. Un nodo saltato non sta sul percorso, quindi il suo campo non
 * alimenta niente: senza questo, `vars.<nome>` resterebbe vuota proprio nel
 * caso in cui il valore si conosce meglio.
 *
 * Il valore non è fisso: si rilegge a ogni valutazione dal path indicato,
 * quindi segue la pagina. E resta il livello più BASSO: un campo con
 * `exportTo` o un'azione `set_var` sullo stesso nome lo sovrascrivono, perché
 * una risposta data dall'operatore batte sempre un valore precompilato.
 */
export interface FlowVarSeed {
  name: string
  /**
   * Path da cui leggere il valore. Ammesse SOLO le root che la pagina fornisce
   * prima dell'avvio: `env.*` e `inputs.*`. Le altre (`answers`, `item`,
   * `vars`) dipendono dall'andamento del wizard e non sarebbero un valore
   * "iniziale": per quelle si usa `set_var` su un passo.
   */
  from: string
}

export interface FlowDefinition {
  id: string
  /** Incrementata a ogni pubblicazione; serve al rollback. */
  version?: number
  status?: 'draft' | 'published' | 'archived'
  title?: string
  description?: string
  /** Punto di innesto nelle pagine, es. `commessa.toolbar`. */
  anchor?: string
  /** Aspetto del bottone che lancia il flow. */
  icon?: string
  color?: string
  /** Il flow è proposto solo se questa condizione è vera. */
  visible_when?: FlowCondition
  inputs?: FlowInput[]
  /** Variabili precompilate da `env.*` / `inputs.*`, disponibili da subito. */
  vars?: FlowVarSeed[]
  nodes: FlowNode[]
  edges?: FlowEdge[]
  /** Unico punto di commit del flow. */
  on_complete?: FlowActionSpec[]
  on_cancel?: FlowActionSpec[]
  metadata?: Record<string, unknown>
}

// ─── Scope ────────────────────────────────────────────────────────────────────

/**
 * Contenitore dei valori leggibili da condizioni e template.
 * Root riconosciute: `item`, `answers`, `vars`, `inputs`, `original`,
 * `changes`, `user`, `setting`, `env`, `$answer`. Un path senza root nota
 * viene risolto su `item` (compatibilità con le condizioni stile trigger).
 */
export interface FlowScope {
  item?: unknown
  /**
   * Variabili del flow, derivate dai campi con `exportTo` sul percorso
   * attraversato. In sola lettura: si dichiarano sul campo, non si assegnano.
   */
  vars?: Record<string, unknown>
  /** Popolata solo durante la valutazione di `optionsWhere`. */
  option?: FlowOption
  answers?: Record<string, unknown>
  inputs?: Record<string, unknown>
  original?: Record<string, unknown>
  changes?: Record<string, unknown>
  user?: unknown
  setting?: unknown
  env?: Record<string, unknown>
  /** Risposta del nodo corrente (usabile in `validate` e nelle `when` degli archi). */
  $answer?: unknown
  [key: string]: unknown
}
